// SharedView — vista pública (sin auth) de un viaje compartido.
// Renderiza un resumen completo: header, días con paradas, restaurantes destacados, tips.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, MapPin, Navigation, Calendar, Star, Plane, Bed, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("es-CL", { day: "numeric", month: "short" });
  } catch { return s; }
}
function priceTxt(p) {
  return ["$", "$$", "$$$", "$$$$"][Math.max(0, Math.min(3, (p || 2) - 1))];
}

export default function SharedView() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("itinerario");

  useEffect(() => {
    let cancelled = false;
    let retried = false;
    setError(null);
    setTrip(null);

    const load = () => {
      // cache-buster: evita que el in-app browser de WhatsApp sirva una respuesta cacheada vieja
      const bust = Date.now();
      fetch(`${API}/api/trips/share/${token}?t=${bust}`, {
        method: "GET",
        cache: "no-store",
        credentials: "omit",
        headers: { "Accept": "application/json" },
      })
        .then(async (r) => {
          if (r.status === 404) {
            const txt = await r.text().catch(() => "");
            const e = new Error(`not-found:${txt}`);
            e.kind = "not-found";
            throw e;
          }
          if (!r.ok) {
            const e = new Error(`http-${r.status}`);
            e.kind = "http";
            throw e;
          }
          return r.json();
        })
        .then((data) => { if (!cancelled) setTrip(data); })
        .catch((err) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error("SharedView fetch error:", err);
          if (err.kind === "not-found") {
            setError({ kind: "not-found", message: "Este viaje ya no está disponible. El link puede haber sido revocado por su autor." });
          } else if (!retried) {
            // Posible navegador in-app de WhatsApp/Instagram → reintenta 1 vez
            retried = true;
            setTimeout(load, 1200);
          } else {
            setError({ kind: "network", message: "No pudimos cargar el viaje. Probablemente el navegador interno de tu app de mensajería esté bloqueando la conexión." });
          }
        });
    };

    load();
    return () => { cancelled = true; };
  }, [token]);

  if (error) {
    const isNotFound = error.kind === "not-found";
    const currentUrl = typeof window !== "undefined" ? window.location.href : "";
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div className="max-w-sm">
          <div className="text-5xl mb-3">{isNotFound ? "🔒" : "📶"}</div>
          <h1 className="text-xl font-bold mb-2">
            {isNotFound ? "Viaje no disponible" : "No se pudo abrir el viaje"}
          </h1>
          <p className="text-sm text-[hsl(var(--muted))] leading-relaxed mb-5">{error.message}</p>

          {!isNotFound && (
            <>
              <p className="text-xs text-[hsl(var(--muted))] mb-4 leading-relaxed">
                Esto suele pasar cuando abres un link directamente desde WhatsApp o Instagram.
                Probá abrirlo en <strong>Safari</strong> o <strong>Chrome</strong>:
              </p>
              <div className="space-y-2 mb-4">
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary w-full"
                  data-testid="shared-view-retry"
                >
                  Reintentar
                </button>
                <a
                  href={currentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost w-full inline-block text-center"
                  data-testid="shared-view-open-external"
                >
                  Abrir en el navegador
                </a>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted))] mt-3 leading-relaxed">
                Si seguís viendo este error: tocá los <strong>tres puntos</strong> arriba a la derecha de WhatsApp
                y elegí <strong>"Abrir en Safari"</strong> o <strong>"Abrir en Chrome"</strong>.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }
  if (!trip) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[hsl(var(--muted))]">Cargando viaje compartido…</div>;
  }

  const data = trip.data || {};
  const days = data.days || [];
  // Normalize places & restaurants: wizard produces arrays, legacy uses keyed objects.
  const places = Array.isArray(data.places)
    ? Object.fromEntries((data.places || []).map((p) => [p.id, p]))
    : (data.places || {});
  const restaurants = Array.isArray(data.restaurants)
    ? (data.restaurants || []).reduce((acc, r) => {
        const city = r.city || r.area || "Otros";
        (acc[city] = acc[city] || []).push(r);
        return acc;
      }, {})
    : (data.restaurants || {});
  const tips = data.tips || {};

  // Build a normalized list of stops as "place:id" strings regardless of input shape.
  const normalizeStops = (day) => {
    const list = [];
    const rawStops = day.stops || [];
    for (const s of rawStops) {
      if (typeof s === "string") list.push(s);
      else if (s && typeof s === "object" && s.id) list.push(`place:${s.id}`);
    }
    for (const rid of day.restaurant_ids || []) {
      list.push(`restaurant:${rid}`);
    }
    return list;
  };

  const openMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`, "_blank");
  };

  return (
    <div className="min-h-screen pb-12" style={{ background: "hsl(35 38% 96%)" }} data-testid="shared-view">
      {/* Hero */}
      <div
        className="relative h-56"
        style={{
          backgroundImage: trip.hero_image ? `url(${trip.hero_image})` : "none",
          backgroundColor: "hsl(var(--ink))",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.75) 100%)" }} />
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/90 text-[hsl(var(--muted))] inline-flex items-center gap-1">
          <Lock size={10} /> Solo lectura
        </div>
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <h1 className="text-2xl font-bold leading-tight">{trip.title}</h1>
          {trip.subtitle && <p className="text-sm opacity-90 mt-0.5">{trip.subtitle}</p>}
          <div className="flex items-center gap-3 text-xs mt-2 opacity-90">
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {formatDate(trip.start_date)} → {formatDate(trip.end_date)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-30 bg-[hsl(var(--bg))] border-b border-[hsl(var(--line))]">
        <div className="container-app flex gap-1 py-2 overflow-x-auto hide-scrollbar">
          {[
            ["itinerario", "Itinerario"],
            ["restaurantes", "Restaurantes"],
            ["vuelos", "Vuelos"],
            ["hoteles", "Hoteles"],
            ["tips", "Tips"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                tab === k ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" : "bg-transparent text-[hsl(var(--muted))]"
              }`}
              data-testid={`shared-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="container-app pt-5">
        {/* ITINERARIO */}
        {tab === "itinerario" && (
          <ul className="space-y-4">
            {days.map((d, i) => (
              <motion.li
                key={d.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * i }}
                className="card-paper p-4"
                data-testid={`shared-day-${d.id}`}
              >
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))]">
                  Día {i + 1} · {d.dayLabel || d.city || ""} · {formatDate(d.date)}
                </p>
                <h3 className="text-base font-bold leading-tight mt-0.5">{d.flag || ""} {d.title}</h3>
                {d.summary && <p className="text-xs text-[hsl(var(--ink-soft))] mt-1.5">{d.summary}</p>}
                {d.narrative && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-[hsl(var(--accent))] cursor-pointer select-none">Leer narrativa completa</summary>
                    <p className="text-xs serif text-[hsl(var(--ink-soft))] leading-[1.6] mt-2">{d.narrative}</p>
                  </details>
                )}
                {(() => {
                  const stops = normalizeStops(d);
                  if (!stops.length) return null;
                  return (
                    <ul className="mt-3 space-y-1.5">
                      {stops.map((ref, k) => {
                        const [kind, id] = (ref || "").split(":");
                        if (kind !== "place") return null;
                        const p = places[id];
                        if (!p) return null;
                        return (
                          <li key={k} className="flex items-center justify-between gap-2 text-xs py-1">
                            <span className="flex items-center gap-1.5 truncate">
                              <span>{p.icon || "📍"}</span>
                              <span className="truncate">{p.name}</span>
                            </span>
                            {p.lat && p.lng && (
                              <button onClick={() => openMaps(p.lat, p.lng)} className="shrink-0 text-[hsl(var(--accent))] inline-flex items-center gap-1" data-testid={`shared-go-${p.id}`}>
                                <Navigation size={11} /> Ir
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </motion.li>
            ))}
          </ul>
        )}

        {/* RESTAURANTES */}
        {tab === "restaurantes" && (
          <div className="space-y-5">
            {Object.keys(restaurants).map((city) => (
              <section key={city}>
                <h3 className="section-title mb-2">{city} · {(restaurants[city] || []).length}</h3>
                <ul className="space-y-2">
                  {(restaurants[city] || []).map((r) => (
                    <li key={r.id} className="card-paper p-3 flex items-start gap-3" data-testid={`shared-rest-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm leading-tight">{r.name}</p>
                        <p className="text-[11px] text-[hsl(var(--muted))]">{r.cuisine} · {r.area}</p>
                        {r.signature && <p className="text-[11px] text-[hsl(var(--ink-soft))] mt-1">› {r.signature}</p>}
                      </div>
                      <div className="text-right shrink-0 text-xs">
                        {r.rating && <p className="inline-flex items-center gap-0.5 font-semibold"><Star size={11} className="fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" /> {r.rating.toFixed(1)}</p>}
                        <p className="font-bold text-[hsl(var(--ink-soft))]">{priceTxt(r.price)}</p>
                        {r.lat && r.lng && (
                          <button onClick={() => openMaps(r.lat, r.lng)} className="mt-1 px-2 py-1 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] text-[10px] font-semibold inline-flex items-center gap-1 active:scale-95">
                            <Navigation size={10} /> Ir
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        {/* VUELOS */}
        {tab === "vuelos" && (
          <ul className="space-y-3">
            {(data.flights || []).map((f) => (
              <li key={f.id} className="card-paper p-4" data-testid={`shared-flight-${f.id}`}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))]">{f.airline} {f.code}</p>
                <div className="flex items-center justify-between mt-1">
                  <div>
                    <p className="text-xl font-bold">{f.from?.code}</p>
                    <p className="text-[11px] text-[hsl(var(--muted))]">{f.from?.city}</p>
                  </div>
                  <Plane size={14} className="text-[hsl(var(--accent))]" />
                  <div className="text-right">
                    <p className="text-xl font-bold">{f.to?.code}</p>
                    <p className="text-[11px] text-[hsl(var(--muted))]">{f.to?.city}</p>
                  </div>
                </div>
                <p className="text-[11px] text-[hsl(var(--ink-soft))] mt-2">{f.depart && new Date(f.depart).toLocaleString("es-CL")} · {f.duration}</p>
              </li>
            ))}
          </ul>
        )}

        {/* HOTELES */}
        {tab === "hoteles" && (
          <ul className="space-y-3">
            {(data.hotels || []).map((h) => (
              <li key={h.id} className="card-paper p-4" data-testid={`shared-hotel-${h.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Bed size={14} className="text-[hsl(var(--accent))] shrink-0" />
                    <span className="font-bold text-sm">{h.name}</span>
                  </div>
                  {h.status === "reservado" && (
                    <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider rounded-full font-semibold shrink-0" style={{ background: "hsl(140 60% 92%)", color: "hsl(140 60% 25%)" }}>Reservado</span>
                  )}
                  {h.status === "pendiente-reservar" && (
                    <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider rounded-full font-semibold shrink-0" style={{ background: "hsl(40 90% 92%)", color: "hsl(30 70% 30%)" }}>Por reservar</span>
                  )}
                </div>
                <p className="text-[11px] text-[hsl(var(--muted))] mt-1">
                  {h.city}{h.nights ? ` · ${h.nights} noches` : ""} · {h.address}
                </p>
                <p className="text-[11px] text-[hsl(var(--ink-soft))] mt-2">
                  Check-in {formatDate(h.checkIn)} · Check-out {formatDate(h.checkOut)}
                </p>
                {h.room_type && <p className="text-[11px] text-[hsl(var(--muted))] mt-1">{h.room_type}</p>}
                {h.code && (
                  <p className="text-[11px] mt-1 font-mono">
                    Res. {h.code}{h.booking_source ? ` · ${h.booking_source}` : ""}
                  </p>
                )}
                {h.payment_method && <p className="text-[11px] text-[hsl(var(--muted))] mt-1">💳 {h.payment_method}</p>}
                {h.phone && <p className="text-[11px] text-[hsl(var(--muted))] mt-1">📞 {h.phone}</p>}
                {h.cancellation && (
                  <p className="text-[11px] text-[hsl(var(--muted))] italic mt-2 leading-snug">
                    {h.cancellation}
                  </p>
                )}
                {h.notes && <p className="text-[11px] text-[hsl(var(--ink-soft))] italic mt-2">{h.notes}</p>}
                <div className="flex gap-3 mt-3">
                  {h.lat && h.lng && (
                    <button onClick={() => openMaps(h.lat, h.lng)} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] font-semibold"><Navigation size={11} /> Cómo llegar</button>
                  )}
                  {h.booking_url && (
                    <a href={h.booking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] font-semibold">
                      Reservar en web →
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* TIPS */}
        {tab === "tips" && (
          <div className="space-y-4">
            {Object.keys(tips).map((city) => {
              const t = tips[city];
              return (
                <section key={city} className="card-paper p-4" data-testid={`shared-tips-${city}`}>
                  <h3 className="font-bold leading-tight">{t.flag} {city}</h3>
                  <p className="text-[11px] text-[hsl(var(--muted))] mt-1">{t.currency}</p>
                  <div className="mt-3 space-y-3">
                    {(t.sections || []).map((s, i) => (
                      <details key={i}>
                        <summary className="text-sm font-semibold cursor-pointer select-none flex items-center gap-2">
                          <span>{s.icon}</span> {s.title}
                        </summary>
                        <ul className="mt-2 space-y-1.5 text-xs text-[hsl(var(--ink-soft))]">
                          {(s.items || []).map((it, j) => (
                            <li key={j} className="flex gap-2">
                              <span className="text-[hsl(var(--accent))]">›</span><span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <p className="text-[10px] text-[hsl(var(--muted))] text-center italic mt-8 inline-flex items-center justify-center gap-1 w-full">
          <Lock size={10} /> Vista de solo lectura · Bitácora
        </p>
      </div>
    </div>
  );
}
